/**
 * Canonical media kinds + validation for Cloudflare R2 uploads.
 * Future kinds (banners, courts, badges, …) add config here only.
 */

/** @typedef {'player' | 'organization' | 'competition'} MediaKind */

export const MEDIA_KINDS = Object.freeze({
  PLAYER: "player",
  ORGANIZATION: "organization",
  COMPETITION: "competition",
});

/** Accepted browser MIME types (reject gif/svg/heic/bmp/tiff). */
export const ACCEPTED_IMAGE_MIME = Object.freeze([
  "image/jpeg",
  "image/png",
  "image/webp",
]);

export const MAX_UPLOAD_BYTES = 5 * 1024 * 1024; // 5 MB pre-compress

/**
 * @type {Record<MediaKind, {
 *   folder: string,
 *   fileName: string,
 *   maxEdge: number,
 *   targetMaxBytes: number,
 *   label: string,
 * }>}
 */
export const MEDIA_KIND_CONFIG = Object.freeze({
  player: {
    folder: "players",
    fileName: "avatar.webp",
    maxEdge: 512,
    targetMaxBytes: 200_000,
    label: "Profile photo",
  },
  organization: {
    folder: "organizations",
    fileName: "logo.webp",
    maxEdge: 512,
    targetMaxBytes: 150_000,
    label: "Organization logo",
  },
  competition: {
    folder: "competitions",
    fileName: "banner.webp",
    maxEdge: 1280,
    targetMaxBytes: 350_000,
    label: "Tournament banner",
  },
});

/**
 * Deterministic R2 object key (no path traversal).
 * @param {MediaKind | string} kind
 * @param {string} entityId
 */
export function mediaObjectPath(kind, entityId) {
  const cfg = MEDIA_KIND_CONFIG[kind];
  if (!cfg) throw new Error("Unknown media kind");
  const id = sanitizeEntityId(entityId);
  if (!id) throw new Error("Missing media id");
  return `${cfg.folder}/${id}/${cfg.fileName}`;
}

/**
 * @param {string} entityId
 */
export function sanitizeEntityId(entityId) {
  const raw = String(entityId || "").trim();
  if (!raw) return "";
  if (raw.includes("..") || raw.includes("/") || raw.includes("\\")) {
    throw new Error("Invalid media id");
  }
  if (!/^[a-zA-Z0-9_:-]{1,128}$/.test(raw)) {
    throw new Error("Invalid media id");
  }
  return raw;
}

/**
 * @param {File | Blob} file
 */
export function assertAcceptedImageFile(file) {
  if (!file) throw new Error("Please choose an image file");
  const type = String(file.type || "").toLowerCase();
  if (!ACCEPTED_IMAGE_MIME.includes(type)) {
    throw new Error("Please use a JPEG, PNG, or WebP image");
  }
  if (typeof file.size === "number" && file.size > MAX_UPLOAD_BYTES) {
    throw new Error("Image must be 5 MB or smaller");
  }
}

/**
 * @param {string} path
 */
export function assertSafeMediaPath(path) {
  const p = String(path || "").replace(/^\/+/, "");
  if (!p || p.includes("..") || p.startsWith("/")) {
    throw new Error("Invalid media path");
  }
  const allowed = Object.values(MEDIA_KIND_CONFIG).some(
    (cfg) =>
      p === `${cfg.folder}/${p.split("/")[1]}/${cfg.fileName}` ||
      p.startsWith(`${cfg.folder}/`)
  );
  // Allow known folders + future folders under a single segment.
  const parts = p.split("/");
  if (parts.length < 2 || parts.length > 4) {
    throw new Error("Invalid media path");
  }
  if (!/^[a-z0-9_-]+$/i.test(parts[0])) {
    throw new Error("Invalid media path");
  }
  if (!allowed && !["defaults", "matches", "banners", "courts", "badges"].includes(parts[0])) {
    // Still allow future-proof folders listed in product plan
  }
  return p;
}
